import {
  PropsWithChildren, ReactNode, useEffect, useRef,
} from 'react';
import { FlattenInterpolation, ThemeProps } from 'styled-components';
import { FormInput, TextInput, ThemeType } from '../../../../..';
import { useModelForm } from '../ModelForm';
import { useIsAutoSave } from '../AutoSave';

export type ModelInputProps = PropsWithChildren<{
  field: string,
  label?: ReactNode,
  placeholder?: string,
  prefix?: ReactNode,
  suffix?: ReactNode,
  labelStyles?: string,
  valueOverride?: string,
  errorOverride?: string,
  mutateValue?: (newValue: string) => any,
  validateValue?: (newValue: string) => boolean,
  disabled?: boolean,
  touchOverride?: boolean,
  onFocus?: () => void,
  onBlur?: (targetValue: string) => void,
  required?: boolean,
  type?: string,
  small?: boolean,
  xs?: boolean,
  onVisibleChange?: (visible: boolean) => void,
  containerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  inline?: boolean,
  suffixCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  isTooltipError?: boolean,
  readOnly?: boolean,
  tooltip?: ReactNode,
  id?: string,
  styles?: string,
  inputWrapperCss?: string,
}>

export function ModelInput({
  field,
  label = '',
  placeholder = '',
  prefix = '',
  suffix = '',
  labelStyles = '',
  valueOverride,
  errorOverride = '',
  mutateValue = (newValue) => newValue,
  disabled = false,
  touchOverride = false,
  onFocus = () => { },
  onBlur = () => { },
  required = false,
  type,
  small = false,
  xs = false,
  onVisibleChange,
  containerCss,
  inline = false,
  suffixCss,
  isTooltipError = false,
  readOnly = false,
  tooltip,
  validateValue = () => true,
  id,
  styles = '',
  inputWrapperCss,
  children,
}: ModelInputProps) {
  const {
    visible, errors, value, update, warnings,
  } = useModelForm(field, valueOverride, errorOverride);

  useEffect(() => {
    onVisibleChange?.(visible);
  }, [visible]);

  const { isAutoSave } = useIsAutoSave();

  const changed = useRef<boolean>(false);

  const blur = (targetValue: string) => {
    onBlur(targetValue);
    if (changed.current && isAutoSave) {
      update({ [field]: mutateValue(targetValue) });
    }
    changed.current = false;
  };

  const onChange = (newValue: string) => {
    changed.current = true;
    return validateValue(newValue) && update(
      { [field]: mutateValue(newValue) },
      isAutoSave,
    );
  };

  return (
    <FormInput visible={visible} css={containerCss}>
      <TextInput
        id={id || field}
        label={label}
        labelStyles={labelStyles}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        mutateValue={mutateValue}
        onFocus={onFocus}
        onBlur={blur}
        prefix={prefix}
        suffix={suffix}
        disabled={disabled}
        touchOverride={touchOverride}
        suffixCss={suffixCss}
        required={required}
        type={type}
        small={small}
        xs={xs}
        visible={visible}
        inline={inline}
        isTooltipError={isTooltipError}
        readOnly={readOnly}
        tooltip={tooltip}
        styles={styles}
        inputWrapperStyle={inputWrapperCss}
        errors={errors}
        warnings={warnings}
      >
        {children}
      </TextInput>
    </FormInput>
  );
}
